import { useRef, useState, useCallback, useEffect } from 'react';
import { Textarea, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { hashtagsService } from '../../services/hashtags';

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  maxLength?: number;
  required?: boolean;
}

/**
 * Textarea with inline #hashtag autocomplete.
 * As the user types `#word`, a dropdown appears with matching suggestions.
 * Selecting a suggestion replaces the `#fragment` in the text.
 * The server will also extract any `#hashtag` tokens on submit as a fallback.
 */
export function HashtagTextarea({
  value,
  onChange,
  label = 'Review',
  placeholder,
  minRows = 5,
  maxRows = 15,
  maxLength,
  required,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [fragmentStart, setFragmentStart] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [rawFragment, setRawFragment] = useState('');
  const [debouncedFragment] = useDebouncedValue(rawFragment, 200);

  // Fetch suggestions when debouncedFragment changes
  useEffect(() => {
    if (!debouncedFragment) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    hashtagsService.autocomplete(debouncedFragment, 6).then((results) => {
      if (!cancelled) {
        setSuggestions(results);
        setActiveIndex(0);
      }
    }).catch(() => {
      if (!cancelled) setSuggestions([]);
    });
    return () => { cancelled = true; };
  }, [debouncedFragment]);

  const detectFragment = useCallback((text: string, cursor: number) => {
    // Walk left from cursor to find a `#` that starts a hashtag token
    let i = cursor - 1;
    while (i >= 0 && /[a-zA-Z0-9_]/.test(text[i])) i--;
    if (i >= 0 && text[i] === '#') {
      const fragment = text.slice(i + 1, cursor);
      if (fragment.length >= 1) {
        setFragmentStart(i);
        setRawFragment(fragment);
        return;
      }
    }
    setFragmentStart(null);
    setRawFragment('');
    setSuggestions([]);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.currentTarget.value;
    onChange(newValue);
    detectFragment(newValue, e.currentTarget.selectionStart ?? newValue.length);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      const el = e.currentTarget;
      detectFragment(el.value, el.selectionStart ?? el.value.length);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length === 0 || fragmentStart === null) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (suggestions[activeIndex]) {
        e.preventDefault();
        applySuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setFragmentStart(null);
    }
  };

  const applySuggestion = (name: string) => {
    if (fragmentStart === null) return;
    const cursor = textareaRef.current?.selectionStart ?? value.length;
    const before = value.slice(0, fragmentStart);
    const after = value.slice(cursor);
    const inserted = `#${name} `;
    const newValue = before + inserted + after;
    onChange(newValue);
    setSuggestions([]);
    setFragmentStart(null);
    setRawFragment('');
    // Restore cursor after the inserted tag
    requestAnimationFrame(() => {
      const pos = before.length + inserted.length;
      textareaRef.current?.setSelectionRange(pos, pos);
      textareaRef.current?.focus();
    });
  };

  const showDropdown = suggestions.length > 0 && fragmentStart !== null;

  return (
    <div style={{ position: 'relative' }}>
      <Textarea
        ref={textareaRef}
        label={label}
        placeholder={placeholder ?? 'Share your thoughts... Use #hashtags to tag topics'}
        required={required}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        minRows={minRows}
        maxRows={maxRows}
        autosize
        maxLength={maxLength}
      />

      {showDropdown && (
        <Paper
          withBorder
          shadow="md"
          style={{
            position: 'absolute',
            zIndex: 200,
            left: 0,
            top: '100%',
            minWidth: 180,
            overflow: 'hidden',
          }}
        >
          <Stack gap={0}>
            {suggestions.map((name, i) => (
              <UnstyledButton
                key={name}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent textarea blur
                  applySuggestion(name);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: i === activeIndex
                    ? 'var(--mantine-color-brand-light)'
                    : 'transparent',
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <Text size="sm">#{name}</Text>
              </UnstyledButton>
            ))}
          </Stack>
        </Paper>
      )}

      <Text size="xs" c="dimmed" mt={4}>
        Tip: Use #hashtags in your review to tag topics
      </Text>
    </div>
  );
}
